import React from "react";
import { CloseIcon } from "@picklebet/ui";
import { MuteIcon } from "@picklebet/ui";
import { UnmuteIcon } from "@picklebet/ui";
import { MultiModal } from "./MultiModal";
import {
  StreamPlayerWithLoader,
  isPickleStream,
  isTwitchStream,
  isBetMakersStream,
  IFrameStream,
} from "@/features/stream/components/StreamPlayerWithLoader";
import { useIsMobile } from "@/hooks/useDisplay";
import { useStreamStore } from "../stores/streamStore";
import { useEvent } from "@/hooks/firestore/betting/useEvent";
import type { Stream } from "../stores/streamStore";
import { EventStatus } from "@/features/shell/components/shared/EventStatus";
import { ChevronRightIcon } from "@picklebet/ui";
import { navigate } from "utilities/navigate";
import { RACING_COMPLETED_STATUSES } from "features/race/util/helpers";

import * as styles from "./FloatingStreamPlayer.module.scss";

const DEFAULT_MEDIA_WIDTH = 520;

const RACING_LIVE_STATUSES = ["CLOSED", "JUMPED"];

const MobileStream = ({
  onClose,
  stream,
}: {
  onClose: () => void;
  stream: Stream;
}) => {
  return (
    <div className={styles.mobileMediaWindow}>
      <div className={styles.inside}>
        <StreamPlayerWithLoader stream={stream} />
      </div>
      <CloseIcon className={styles.closeTop} onClick={onClose} />
    </div>
  );
};

const DesktopStream = ({
  stream,
  onClose,
}: {
  stream: Stream;
  onClose: () => void;
}) => {
  const toggleMuteStream = useStreamStore((state) => state.toggleMuteStream);
  const { event } = useEvent(stream.eventId, "all");

  const isLiveRacingChannel =
    isBetMakersStream(stream.url) && event?.hub === "racing";

  const sport = event?.sport ?? stream.sport;
  const title = isLiveRacingChannel
    ? "Live Racing Channel"
    : event?.eventName ?? stream.title ?? "";
  const isLive =
    event?.hub === "racing"
      ? RACING_LIVE_STATUSES.includes(event?.status ?? "")
      : ["LIVE", "SUSPENDED"].includes(event?.status ?? "");
  const eventUrl =
    stream.eventUrl ??
    (event && "eventId" in event
      ? `${event.hub === "sports" ? "/sports" : ""}/betting/match/${event.eventId}`
      : undefined);

  const subtitle = isLiveRacingChannel ? (
    <span className={styles.subtitleText}>
      {event?.eventName}
      {isLive || RACING_COMPLETED_STATUSES.includes(event?.status ?? "")
        ? ""
        : " in "}
      {!isLive && !RACING_COMPLETED_STATUSES.includes(event?.status ?? "") && (
        <EventStatus
          clampClassName={styles.subtitleCountdown}
          status={event.status}
          scheduledStartTime={event.scheduledStartTime}
          shape="rounded"
          variant="dangerSoft"
        />
      )}
      <div
        className={styles.eventLink}
        onClick={(e) => {
          const url = `/racing/betting/race/${event?.meetingId}/${event?.id}`;
          e.stopPropagation();
          if (window.location.pathname.includes(url)) {
            return;
          }
          navigate(url);
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <ChevronRightIcon />
      </div>
    </span>
  ) : undefined;

  return (
    <div>
      <MultiModal
        width={DEFAULT_MEDIA_WIDTH}
        title={title}
        sport={sport}
        isLive={isLive}
        eventUrl={eventUrl}
        onClose={onClose}
        maxSize={isPickleStream(stream.url) ? 33 : 80}
        ratio={{ width: 16, height: 9 }}
        subtitle={subtitle}
        isLiveRacingChannel={isLiveRacingChannel}
      >
        {isTwitchStream(stream.url) || isBetMakersStream(stream.url) ? (
          <IFrameStream streamUrl={stream.url} />
        ) : (
          <div className={styles.baseMediaHolder}>
            {isPickleStream(stream.url) && (
              <div className={styles.customMediaControls}>
                {stream.muted ? (
                  <MuteIcon
                    className={styles.icon}
                    onClick={() => toggleMuteStream(stream)}
                  />
                ) : (
                  <UnmuteIcon
                    className={styles.icon}
                    onClick={() => toggleMuteStream(stream)}
                  />
                )}
              </div>
            )}
            <StreamPlayerWithLoader stream={stream} />
          </div>
        )}
      </MultiModal>
    </div>
  );
};

const DesktopStreams = ({
  onClose,
  streams,
}: {
  onClose: (stream: Stream) => void;
  streams: Stream[];
}) => {
  return streams.map((stream) => (
    <DesktopStream
      key={stream.eventId}
      stream={stream}
      onClose={() => onClose(stream)}
    />
  ));
};

export const FloatingStreamPlayer = () => {
  const isMobile = useIsMobile();
  const streams = useStreamStore((state) => state.streams);
  const closeStream = useStreamStore((state) => state.closeStream);
  const closeAllStreams = useStreamStore((state) => state.closeAllStreams);

  if (!streams.length) {
    return null;
  }

  if (isMobile) {
    return <MobileStream onClose={closeAllStreams} stream={streams[0]} />;
  }

  return <DesktopStreams onClose={closeStream} streams={streams} />;
};
